import React, { useEffect, useState, useCallback } from "react";
import { api } from "../context/AuthContext";
import { useI18n } from "../context/I18nContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "./ui/dialog";
import { Switch } from "./ui/switch";
import { Checkbox } from "./ui/checkbox";
import { PaperPlaneTilt, FilePdf, Plus, MagnifyingGlass, CheckCircle, XCircle, MapPin, Bank, FileXls, WhatsappLogo, Truck } from "@phosphor-icons/react";
import { toast } from "sonner";
import { PROVINCE_NAMES, provinceColor } from "../lib/provinces";

const money = (v, cur) => `${cur === "USD" ? "US$" : "RD$"} ${Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const remittanceWaLink = (r) => {
  const digits = String(r.recipient_phone || "").replace(/\D/g, "");
  const num = digits.length === 10 ? "1" + digits : digits;
  const place = r.city ? `${r.city}, ${r.province}` : r.province;
  const msg = `Grupo Estrella Hernández Inversiones - Aviso de envío.\nTienes un envío listo para retirar en efectivo.\n• Referencia: ${r.reference}\n• Monto: ${money(r.amount, r.currency)}\n• Retiro en: ${place}\nPresenta tu cédula en la sucursal de destino con este código. ¡Gracias!`;
  return `https://wa.me/${num}?text=${encodeURIComponent(msg)}`;
};

const ProvinceBadge = ({ name }) => (
  <span className="inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full text-white" style={{ backgroundColor: provinceColor(name) }} data-testid={`province-badge-${name}`}>
    <MapPin size={12} weight="fill" /> {name}
  </span>
);

const StatusBadge = ({ status }) => {
  const { t } = useI18n();
  return status === "delivered" ? (
    <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700"><CheckCircle size={13} weight="fill" /> {t("rm_delivered")}</span>
  ) : (
    <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-600"><XCircle size={13} weight="fill" /> {t("rm_not_received")}</span>
  );
};

const NewRemittanceDialog = ({ fees, enabled, onDone }) => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [method, setMethod] = useState("cash");
  const [accId, setAccId] = useState("");
  const [cashCurrency, setCashCurrency] = useState("DOP");
  const [amount, setAmount] = useState("");
  const [freeSend, setFreeSend] = useState(false);
  const [deliveryAmount, setDeliveryAmount] = useState("");
  const [rName, setRName] = useState("");
  const [rId, setRId] = useState("");
  const [rPhone, setRPhone] = useState("");
  const [province, setProvince] = useState("");
  const [city, setCity] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) api.get("/admin/savings-accounts").then((r) => setAccounts(r.data)).catch(() => {});
  }, [open]);

  const selectedAcc = accounts.find((a) => a.id === accId);
  const cur = method === "savings" && selectedAcc ? selectedAcc.currency : cashCurrency;
  const fee = freeSend ? 0 : (cur === "USD" ? (fees?.usd || 0) : (fees?.dop || 0));
  const amt = Number(amount) || 0;
  const total = amt + Number(fee || 0);

  const submit = async () => {
    if (!rName.trim()) { toast.error(t("rm_need_recipient")); return; }
    if (!province) { toast.error(t("rm_need_province")); return; }
    if (amt <= 0) { toast.error(t("rm_need_amount")); return; }
    if (method === "savings" && !accId) { toast.error(t("rm_need_account")); return; }
    if (freeSend && !(Number(deliveryAmount) > 0)) { toast.error(t("rm_need_delivery")); return; }
    setBusy(true);
    try {
      await api.post("/admin/remittances", {
        payment_method: method, from_account_id: method === "savings" ? accId : null,
        amount: amt, currency: cur, free_send: freeSend, delivery_amount: freeSend ? Number(deliveryAmount) : null,
        recipient_name: rName, recipient_id: rId, recipient_phone: rPhone,
        province, city, note,
      });
      toast.success(t("rm_created"));
      setOpen(false);
      setMethod("cash"); setAccId(""); setAmount(""); setFreeSend(false); setDeliveryAmount(""); setRName(""); setRId(""); setRPhone(""); setProvince(""); setCity(""); setNote("");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || t("error")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="new-remittance-btn" disabled={!enabled} title={!enabled ? t("rm_disabled_msg") : ""} className="bg-emerald-800 hover:bg-emerald-900 gap-2 h-11"><Plus size={18} weight="bold" /> {t("rm_new")}</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{t("rm_new")}</DialogTitle><DialogDescription>{t("remittances_sub")}</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <label className="flex items-center gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 cursor-pointer" data-testid="rm-free-label">
            <Checkbox data-testid="rm-free" checked={freeSend} onCheckedChange={(v) => setFreeSend(!!v)} />
            <span className="text-sm font-semibold text-amber-800">{t("rm_free_send")}</span>
            <span className="text-xs text-amber-600">— {t("rm_free_hint")}</span>
          </label>
          <div>
            <Label>{t("rm_payment_method")}</Label>
            <Select value={method} onValueChange={(v) => { setMethod(v); setAccId(""); }}>
              <SelectTrigger data-testid="rm-method" className="mt-1 h-11"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">{t("rm_cash")}</SelectItem>
                <SelectItem value="savings">{t("rm_from_savings")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {method === "savings" && (
            <div>
              <Label>{t("rm_account")}</Label>
              <Select value={accId} onValueChange={setAccId}>
                <SelectTrigger data-testid="rm-account" className="mt-1 h-11"><SelectValue placeholder={t("rm_select_account")} /></SelectTrigger>
                <SelectContent>
                  {accounts.map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.account_number} · {a.full_name} · {money(a.balance, a.currency)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {method === "cash" && (
            <div>
              <Label>{t("rm_currency")}</Label>
              <Select value={cashCurrency} onValueChange={setCashCurrency}>
                <SelectTrigger data-testid="rm-currency" className="mt-1 h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="DOP">RD$ (DOP)</SelectItem>
                  <SelectItem value="USD">US$ (USD)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t("rm_amount")} ({cur === "USD" ? "US$" : "RD$"})</Label>
              <Input data-testid="rm-amount" type="number" min="0" step="0.01" className="mt-1 h-11 font-mono-num" value={amount} onChange={(e) => setAmount(e.target.value)} />
            </div>
            <div>
              <Label>{t("rm_fee")}</Label>
              <Input readOnly value={money(fee, cur)} className="mt-1 h-11 font-mono-num bg-slate-50" />
            </div>
          </div>
          <div className="rounded-lg bg-emerald-50 px-4 py-2.5 flex justify-between items-center">
            <span className="text-sm text-emerald-700 font-semibold">{t("rm_total_charge")}</span>
            <span className="font-mono-num font-bold text-emerald-900" data-testid="rm-total">{money(total, cur)}</span>
          </div>
          {freeSend && (
            <div>
              <Label>{t("rm_delivery_amount")} (RD$)</Label>
              <Input data-testid="rm-delivery" type="number" min="0" step="0.01" className="mt-1 h-11 font-mono-num" value={deliveryAmount} onChange={(e) => setDeliveryAmount(e.target.value)} />
            </div>
          )}
          <div className="border-t border-gray-100 pt-3 space-y-3">
            <div>
              <Label>{t("rm_recipient")}</Label>
              <Input data-testid="rm-recipient-name" className="mt-1 h-11" value={rName} onChange={(e) => setRName(e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t("rm_recipient_id")}</Label><Input data-testid="rm-recipient-id" className="mt-1 h-11" value={rId} onChange={(e) => setRId(e.target.value)} /></div>
              <div><Label>{t("rm_recipient_phone")}</Label><Input data-testid="rm-recipient-phone" className="mt-1 h-11" value={rPhone} onChange={(e) => setRPhone(e.target.value)} /></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>{t("rm_province")}</Label>
                <Select value={province} onValueChange={setProvince}>
                  <SelectTrigger data-testid="rm-province" className="mt-1 h-11"><SelectValue placeholder={t("rm_select_province")} /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {PROVINCE_NAMES.map((p) => (
                      <SelectItem key={p} value={p}>
                        <span className="inline-flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: provinceColor(p) }} /> {p}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>{t("rm_city")}</Label><Input data-testid="rm-city" className="mt-1 h-11" value={city} onChange={(e) => setCity(e.target.value)} /></div>
            </div>
            <div><Label>{t("rm_note")}</Label><Textarea data-testid="rm-note" className="mt-1" value={note} onChange={(e) => setNote(e.target.value)} /></div>
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="rm-submit" onClick={submit} disabled={busy} className="bg-emerald-800 hover:bg-emerald-900">{busy ? t("rm_sending") : t("rm_confirm")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const RemittancesPanel = () => {
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fee, setFee] = useState(0);
  const [feeInput, setFeeInput] = useState("");
  const [feeUsd, setFeeUsd] = useState(0);
  const [feeUsdInput, setFeeUsdInput] = useState("");
  const [overdueInput, setOverdueInput] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [savingFee, setSavingFee] = useState(false);
  const [q, setQ] = useState("");
  const [province, setProvince] = useState("all");
  const [status, setStatus] = useState("all");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [downloading, setDownloading] = useState("");

  const params = useCallback(() => {
    const p = {};
    if (q.trim()) p.q = q.trim();
    if (province !== "all") p.province = province;
    if (status !== "all") p.status = status;
    if (start) p.start = start;
    if (end) p.end = end;
    if (overdueOnly) p.overdue = true;
    return p;
  }, [q, province, status, start, end, overdueOnly]);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/remittances", { params: params() }).then((r) => setRows(r.data)).catch(() => setRows([])).finally(() => setLoading(false));
  }, [params]);

  const loadFee = useCallback(() => {
    api.get("/admin/remittance-settings").then((r) => { setFee(r.data.fee); setFeeInput(String(r.data.fee)); setFeeUsd(r.data.fee_usd || 0); setFeeUsdInput(String(r.data.fee_usd ?? 0)); setOverdueInput(String(r.data.overdue_days ?? 2)); setEnabled(r.data.enabled !== false); }).catch(() => {});
  }, []);

  useEffect(() => { const id = setTimeout(load, 300); return () => clearTimeout(id); }, [load]);
  useEffect(() => { loadFee(); }, [loadFee]);

  const toggleEnabled = async (val) => {
    setEnabled(val);
    try { await api.put("/admin/remittance-settings", { amount: Number(feeInput) || 0, enabled: val }); toast.success(val ? t("rm_enabled_on") : t("rm_enabled_off")); }
    catch (e) { toast.error(t("error")); setEnabled(!val); }
  };

  const saveFee = async () => {
    setSavingFee(true);
    try { const r = await api.put("/admin/remittance-settings", { amount: Number(feeInput) || 0, amount_usd: Number(feeUsdInput) || 0, overdue_days: Math.max(1, Number(overdueInput) || 2) }); setFee(r.data.fee); setFeeUsd(r.data.fee_usd || 0); toast.success(t("rm_fee_saved")); }
    catch (e) { toast.error(t("error")); }
    finally { setSavingFee(false); }
  };

  const toggleStatus = async (r) => {
    const next = r.status === "delivered" ? "pending" : "delivered";
    try { await api.put(`/admin/remittances/${r.id}/status`, { status: next }); load(); }
    catch (e) { toast.error(t("error")); }
  };

  const downloadReceipt = async (r) => {
    try {
      const res = await api.get(`/admin/remittances/${r.id}/receipt`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `envio_${r.reference}.pdf`; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(t("error")); }
  };

  const exportReport = async (kind) => {
    setDownloading(kind);
    try {
      const path = kind === "excel" ? "/admin/remittances/export" : "/admin/remittances/report/pdf";
      const res = await api.get(path, { params: params(), responseType: "blob" });
      const isPdf = kind === "pdf";
      const url = window.URL.createObjectURL(new Blob([res.data], { type: isPdf ? "application/pdf" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const a = document.createElement("a"); a.href = url; a.download = `reporte_envios.${isPdf ? "pdf" : "xlsx"}`; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(t("error")); }
    finally { setDownloading(""); }
  };

  return (
    <div className="space-y-6" data-testid="remittances-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-lg bg-emerald-100 flex items-center justify-center"><PaperPlaneTilt size={24} weight="duotone" className="text-emerald-800" /></div>
          <div>
            <h2 className="font-display font-bold text-emerald-900 text-xl">{t("remittances_title")}</h2>
            <p className="text-sm text-slate-500">{t("remittances_sub")}</p>
          </div>
        </div>
        <NewRemittanceDialog fees={{ dop: fee, usd: feeUsd }} enabled={enabled} onDone={load} />
      </div>

      <div className="flex justify-between items-center gap-2 flex-wrap">
        <Button data-testid="remittance-overdue-toggle" onClick={() => setOverdueOnly((v) => !v)} variant={overdueOnly ? "default" : "outline"} className={`gap-2 h-10 ${overdueOnly ? "bg-red-500 hover:bg-red-600" : "border-red-300 text-red-600 hover:bg-red-50"}`}>
          <Truck size={16} weight="bold" /> {overdueOnly ? t("rm_overdue_only_on") : t("rm_overdue_only")}
        </Button>
        <div className="flex gap-2 flex-wrap">
          <Button data-testid="remittances-excel-btn" onClick={() => exportReport("excel")} disabled={!!downloading} variant="outline" className="border-emerald-300 text-emerald-800 hover:bg-emerald-50 gap-2 h-10"><FileXls size={16} weight="bold" /> {downloading === "excel" ? t("generating") : t("download_excel")}</Button>
          <Button data-testid="remittances-report-pdf-btn" onClick={() => exportReport("pdf")} disabled={!!downloading} variant="outline" className="border-emerald-300 text-emerald-800 hover:bg-emerald-50 gap-2 h-10"><FilePdf size={16} weight="bold" /> {downloading === "pdf" ? t("generating") : t("download_pdf")}</Button>
        </div>
      </div>

      {/* Enable toggle */}
      <div className={`rounded-xl border shadow-sm p-4 flex items-center justify-between gap-3 ${enabled ? "bg-white border-gray-100" : "bg-red-50 border-red-200"}`} data-testid="remittance-enabled-card">
        <div>
          <h3 className="font-display font-bold text-emerald-900">{t("rm_enabled_title")}</h3>
          <p className="text-xs text-slate-500">{enabled ? t("rm_enabled_on_hint") : t("rm_disabled_msg")}</p>
        </div>
        <Switch data-testid="remittance-enabled-switch" checked={enabled} onCheckedChange={toggleEnabled} />
      </div>

      {/* Fee setting */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 flex items-end justify-between flex-wrap gap-3" data-testid="remittance-fee-card">
        <div className="flex items-center gap-2">
          <Bank size={20} weight="duotone" className="text-emerald-700" />
          <div>
            <h3 className="font-display font-bold text-emerald-900">{t("rm_fee_title")}</h3>
            <p className="text-xs text-slate-500">{t("rm_fee_hint")}</p>
          </div>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div>
            <Label className="text-xs text-slate-500">{t("rm_fee")} (RD$)</Label>
            <Input data-testid="remittance-fee-input" type="number" min="0" step="1" value={feeInput} onChange={(e) => setFeeInput(e.target.value)} className="h-10 w-[110px] font-mono-num" />
          </div>
          <div>
            <Label className="text-xs text-slate-500">{t("rm_fee")} (US$)</Label>
            <Input data-testid="remittance-fee-usd-input" type="number" min="0" step="1" value={feeUsdInput} onChange={(e) => setFeeUsdInput(e.target.value)} className="h-10 w-[110px] font-mono-num" />
          </div>
          <div>
            <Label className="text-xs text-slate-500">{t("rm_overdue_days_label")}</Label>
            <Input data-testid="remittance-overdue-input" type="number" min="1" max="60" step="1" value={overdueInput} onChange={(e) => setOverdueInput(e.target.value)} className="h-10 w-[110px] font-mono-num" />
          </div>
          <Button data-testid="remittance-fee-save" onClick={saveFee} disabled={savingFee} className="bg-emerald-800 hover:bg-emerald-900 h-10">{t("save")}</Button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1 flex-1 min-w-[200px]">
          <Label className="text-xs text-slate-500">{t("search")}</Label>
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="remittance-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("rm_search_ph")} className="h-10 pl-9" />
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <Label className="text-xs text-slate-500">{t("rm_province")}</Label>
          <Select value={province} onValueChange={setProvince}>
            <SelectTrigger data-testid="remittance-province-filter" className="h-10 w-[200px]"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-64">
              <SelectItem value="all">{t("rm_all_provinces")}</SelectItem>
              {PROVINCE_NAMES.map((p) => (
                <SelectItem key={p} value={p}><span className="inline-flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: provinceColor(p) }} /> {p}</span></SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1">
          <Label className="text-xs text-slate-500">{t("status")}</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="remittance-status-filter" className="h-10 w-[160px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("rm_all_status")}</SelectItem>
              <SelectItem value="pending">{t("rm_not_received")}</SelectItem>
              <SelectItem value="delivered">{t("rm_delivered")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1">
          <Label className="text-xs text-slate-500">{t("from_date")}</Label>
          <Input data-testid="remittance-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} className="h-10 w-[160px]" />
        </div>
        <div className="flex flex-col gap-1">
          <Label className="text-xs text-slate-500">{t("to_date")}</Label>
          <Input data-testid="remittance-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="h-10 w-[160px]" />
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-x-auto" data-testid="remittances-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-slate-400 border-b border-gray-100">
              <th className="px-4 py-2.5 font-medium">{t("date")}</th>
              <th className="px-4 py-2.5 font-medium">{t("reference")}</th>
              <th className="px-4 py-2.5 font-medium">{t("rm_recipient")}</th>
              <th className="px-4 py-2.5 font-medium">{t("rm_province")}</th>
              <th className="px-4 py-2.5 font-medium text-right">{t("rm_amount")}</th>
              <th className="px-4 py-2.5 font-medium text-center">{t("status")}</th>
              <th className="px-4 py-2.5 font-medium text-right">{t("actions")}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">{t("loading")}</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400" data-testid="remittances-empty">{t("rm_empty")}</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} className="border-b border-gray-50 last:border-0" data-testid={`remittance-row-${r.id}`}>
                <td className="px-4 py-2.5 text-slate-600">{(r.created_at || "").slice(0, 10)}</td>
                <td className="px-4 py-2.5 font-mono-num text-slate-500">{r.reference}</td>
                <td className="px-4 py-2.5">
                  <div className="text-emerald-900 font-semibold">{r.recipient_name}</div>
                  <div className="text-xs text-slate-400">{r.recipient_id || ""} {r.recipient_phone ? "· " + r.recipient_phone : ""}{r.city ? " · " + r.city : ""}</div>
                </td>
                <td className="px-4 py-2.5"><ProvinceBadge name={r.province} /></td>
                <td className="px-4 py-2.5 text-right font-mono-num font-bold text-emerald-800">{money(r.amount, r.currency)}<div className="text-[10px] text-slate-400 font-normal">+{money(r.fee, r.currency)} {t("rm_fee_lower")}</div></td>
                <td className="px-4 py-2.5 text-center"><StatusBadge status={r.status} /></td>
                <td className="px-4 py-2.5 text-right whitespace-nowrap">
                  {r.recipient_phone ? (
                    <a data-testid={`remittance-wa-${r.id}`} href={remittanceWaLink(r)} target="_blank" rel="noopener noreferrer" title={t("rm_notify_whatsapp")} className="inline-flex items-center justify-center rounded-md bg-[#25D366] px-2 py-1.5 text-white hover:bg-[#1eb85a] mr-1.5 align-middle"><WhatsappLogo size={15} weight="fill" /></a>
                  ) : null}
                  <Button data-testid={`remittance-toggle-${r.id}`} size="sm" variant="outline" onClick={() => toggleStatus(r)} className={`gap-1 mr-1.5 ${r.status === "delivered" ? "border-amber-300 text-amber-700 hover:bg-amber-50" : "border-emerald-300 text-emerald-700 hover:bg-emerald-50"}`}>
                    {r.status === "delivered" ? t("rm_mark_pending") : t("rm_mark_delivered")}
                  </Button>
                  <Button data-testid={`remittance-receipt-${r.id}`} size="sm" variant="outline" onClick={() => downloadReceipt(r)} className="border-emerald-300 text-emerald-800 hover:bg-emerald-50 gap-1"><FilePdf size={14} weight="bold" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
